import StarIcon from 'assets/icons/StarIcon';
import WebIcon from 'assets/icons/WebIcon';
import Fields, { BrandFormValues, GeneratingFieldWrap } from 'Components/brands/Fields';
import FieldInstruction from 'Components/Common/FieldInstruction';
import withRouter from 'Components/Common/withRouter';
import { FIELDS_INSTRUCTIONS } from 'constants/fieldsInstructions';
import { PRIVILEGE } from 'constants/privilege';
import { useFirebase } from 'contexts/FirebaseAuthContext';
import { useFormik } from 'formik';
import withAccessControl from 'hoc/withAccessControl';
import useDocumentTitle from 'hooks/useDocumentTitle';
import { parse } from 'best-effort-json-parser';
import { collection, doc, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import React, { useEffect, useMemo, useState, KeyboardEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Col, Form, FormFeedback, FormGroup, Input, Label, Spinner, Button } from 'reactstrap';
import { createNewBrand, generateBrandDetails } from 'services/api/brands';
import { fetchBrandList } from 'slices/brand/list/thunk';
import { AppDispatch, RootState } from 'store';
import { GeneratedBrandDetails, NewBrand as NewBrandPayload } from 'types/brand';
import { getErrorMessage } from 'utils/helpers';
import './style.scss';
import * as Yup from 'yup';

const mapGeneratedDetailsToForm = (details: GeneratedBrandDetails): Partial<BrandFormValues> => {
    const updates: Partial<BrandFormValues> = {};

    if (details.description) {
        updates.description = details.description;
    }
    if (details.w?.n) {
        updates.website_url = details.w.n;
    }
    if (details.ig?.n) {
        updates.ig_handle = details.ig.n;
    }
    return updates;
};

const NewBrand = (props: { router: { navigate: (path: string) => void } }) => {
    useDocumentTitle('Create Brand');

    const selectedAgency = useSelector((state: RootState) => state.auth.selectedAgency);
    const dispatch: AppDispatch = useDispatch();
    const { realtimeUpdatesDB } = useFirebase();
    const [generating, setGenerating] = useState(false);
    const [saving, setSaving] = useState(false);
    const [brandLogo, setBrandLogo] = useState<File | null>(null);
    const [streamUuid, setStreamUuid] = useState<string | null>(null);
    const [aiTaskChipText, setAiTaskChipText] = useState<string | null>(null);
    const [aiTaskStatus, setAiTaskStatus] = useState<string | null>(null);
    const [aiTaskResult, setAiTaskResult] = useState<string | null>(null);
    const [brandDetails, setBrandDetails] = useState<GeneratedBrandDetails | null>(null);
    const [websiteConfirmation, setWebsiteConfirmation] = useState<'accepted' | 'rejected' | null>(null);

    const defaultBrand: BrandFormValues = {
        name: '',
        description: '',
        website_url: '',
        ig_handle: '',
        img_url: null,
    };

    const formFields = useFormik({
        enableReinitialize: true,
        initialValues: defaultBrand,
        validationSchema: Yup.object({
            name: Yup.string().required("Brand name field can't be empty").max(50, 'Maximum 50 characters are allowed'),
            description: Yup.string().required("Brand description field can't be empty"),
            website_url: Yup.string().required("Brand website field can't be empty"),
        }),
        onSubmit: async (brand) => {
            try {
                if (brandLogo && brandLogo.size > 2 * 1024 * 1024) {
                    formFields.setFieldError('img_url', 'Image size must not exceed 2MB');
                    return;
                }

                if (!selectedAgency?.uuid) {
                    toast.error('No agency selected');
                    return;
                }

                setSaving(true);
                const newBrand: NewBrandPayload = { ...brand, ...(brandLogo ? { img: brandLogo } : {}) };
                await createNewBrand(selectedAgency.uuid, newBrand);
                dispatch(fetchBrandList({ page: 1, force: true }));
                toast.success('Brand created successfully!');
                props.router.navigate('/brand');
            } catch (error) {
                toast.error(getErrorMessage(error, 'Failed to create new brand'));
            } finally {
                setSaving(false);
            }
        },
    });

    const showWebsiteOnly = !!streamUuid && websiteConfirmation === null;
    const showAllFields = websiteConfirmation !== null;
    const isGeneratingDetails = !!streamUuid && aiTaskStatus !== 'completed';
    const isAwaitingWebsiteConfirmation = websiteConfirmation === null && brandDetails;
    const isFormReady = websiteConfirmation !== null;

    useEffect(() => {
        if (!streamUuid || !realtimeUpdatesDB || aiTaskResult) return;

        const q = query(
            collection(realtimeUpdatesDB, 'ai_tasks', streamUuid, 'events'),
            orderBy('updated_at', 'desc'),
            limit(1)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const events = snapshot.docs.map((docSnapshot) => ({
                id: docSnapshot.id,
                ...docSnapshot.data(),
            })) as Array<{ id: string; event?: string }>;
            setAiTaskChipText(events[0]?.event ?? null);
        });

        return () => unsubscribe();
    }, [streamUuid, realtimeUpdatesDB, aiTaskResult]);

    useEffect(() => {
        if (!streamUuid || !realtimeUpdatesDB) return;

        const unsubscribe = onSnapshot(
            doc(realtimeUpdatesDB, 'ai_tasks', streamUuid),
            (snapshot) => {
                if (!snapshot.exists()) return;

                const data = snapshot.data();
                setAiTaskStatus(data?.status ?? null);
                setAiTaskResult(data?.result ?? null);
            },
            (error) => {
                console.error('brand ai task listener error:', error);
                toast.error('Failed to receive brand details update');
            }
        );

        return () => unsubscribe();
    }, [streamUuid, realtimeUpdatesDB]);

    useEffect(() => {
        if (!aiTaskResult) return;

        const parsed = parse(aiTaskResult) as GeneratedBrandDetails;
        setBrandDetails(parsed);
    }, [aiTaskResult]);

    useEffect(() => {
        if (!brandDetails || !isGeneratingDetails) return;

        const websiteUrl = brandDetails.w?.n;
        if (websiteUrl && formFields.values.website_url !== websiteUrl) {
            formFields.setFieldValue('website_url', websiteUrl);
        }
    }, [brandDetails, isGeneratingDetails, formFields.values.website_url, formFields.setFieldValue]);

    const handleWebsiteConfirm = () => {
        if (brandDetails) {
            const updates = mapGeneratedDetailsToForm(brandDetails);
            formFields.setValues({ ...formFields.values, ...updates });
        }
        setWebsiteConfirmation('accepted');
    };

    const handleWebsiteReject = () => {
        formFields.setValues({
            ...formFields.values,
            website_url: '',
            description: '',
            ig_handle: '',
            img_url: null,
        });
        setBrandLogo(null);
        setWebsiteConfirmation('rejected');
    };

    const handleGenerateBrandDetails = async () => {
        const errors = await formFields.validateForm();
        if (errors.name) {
            formFields.setFieldTouched('name', true);
            return;
        }

        if (!selectedAgency?.uuid) {
            toast.error('No agency selected');
            return;
        }

        setGenerating(true);
        try {
            const response = await generateBrandDetails(selectedAgency.uuid, formFields.values.name.trim());

            if (!response.status || !response.stream_uuid) {
                toast.error(response.message || 'Failed to fetch brand details');
                return;
            }

            setStreamUuid(response.stream_uuid);
            setAiTaskChipText(null);
            setAiTaskStatus(null);
            setAiTaskResult(null);
            setBrandDetails(null);
            setWebsiteConfirmation(null);
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to fetch brand details'));
        } finally {
            setGenerating(false);
        }
    };

    const handleNameKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        if (generating || !formFields.values.name.trim() || streamUuid) return;
        handleGenerateBrandDetails();
    };

    const handleResetBrand = () => {
        setGenerating(false);
        setSaving(false);
        setBrandLogo(null);
        setStreamUuid(null);
        setAiTaskChipText(null);
        setAiTaskStatus(null);
        setAiTaskResult(null);
        setBrandDetails(null);
        setWebsiteConfirmation(null);
        formFields.resetForm({ values: defaultBrand });
    };

    const showBrandNameClear = !!streamUuid && !!formFields.values.name.trim() && !generating;
    const showBrandNameConfirm = !streamUuid && !generating && !!formFields.values.name.trim();

    const aiChipLabel = useMemo(() => aiTaskChipText ?? 'Analysing with AI', [aiTaskChipText]);

    const igProbables = useMemo(() => {
        const probables = brandDetails?.ig_probables ?? [];
        const primary = brandDetails?.ig?.n?.trim();
        if (!primary) return probables;
        return probables.includes(primary) ? probables : [primary, ...probables];
    }, [brandDetails]);

    return (
        <div className="page-content">
            <div className="page-content-inner page-spacing">
                <div className="d-flex w-100 justify-content-between align-items-center pt-1 pb-5">
                    <h4 className="page-title">Create Brand</h4>
                </div>
                <Form
                    className="needs-validation"
                    onSubmit={(e) => {
                        e.preventDefault();
                        formFields.handleSubmit();
                        return false;
                    }}
                >
                    <div className={`brand-create-card ${(isAwaitingWebsiteConfirmation || websiteConfirmation === 'rejected') ? '' : 'show-ai-status'} position-relative mt-2`}>
                        <div className="white-blur-center top"></div>
                        <div className="brand-create-card__body">
                            <FormGroup row className="mb-0 align-items-center">
                                <Col sm={4} className="my-1">
                                    <div className="d-flex gap-3">
                                        <div className="w-40px">
                                            <StarIcon className="me-2" />
                                        </div>
                                        <div className="d-flex flex-column gap-2">
                                            <Label htmlFor="name" className="form-label input-label mb-0">
                                                Brand Name <span className="text-danger">*</span>
                                            </Label>
                                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.BRAND.NAME} />
                                        </div>
                                    </div>
                                </Col>
                                <Col sm={8} className="my-1">
                                    <div className="position-relative brand-name-input">
                                        <Input
                                            type="text"
                                            name="name"
                                            id="name"
                                            className={`form-control ${(showBrandNameClear || showBrandNameConfirm) ? 'pe-5' : ''}`}
                                            onChange={formFields.handleChange}
                                            onBlur={formFields.handleBlur}
                                            onKeyDown={handleNameKeyDown}
                                            value={formFields.values.name}
                                            invalid={formFields.touched.name && formFields.errors.name ? true : false}
                                            placeholder="Enter Brand name"
                                            disabled={generating || !!streamUuid}
                                        />
                                        {showBrandNameConfirm && (
                                            <button
                                                type="button"
                                                className="brand-field-confirm-btn"
                                                onClick={handleGenerateBrandDetails}
                                                aria-label="Confirm brand name"
                                            >
                                                <i className="bx bx-check" />
                                            </button>
                                        )}
                                        {showBrandNameClear && (
                                            <button
                                                type="button"
                                                className="brand-field-clear-btn"
                                                onClick={handleResetBrand}
                                                aria-label="Reset brand details"
                                            >
                                                <i className="bx bx-x" />
                                            </button>
                                        )}
                                    </div>
                                    {formFields.touched.name && formFields.errors.name && (
                                        <FormFeedback type="invalid" className="d-flex">
                                            {formFields.errors.name}
                                        </FormFeedback>
                                    )}
                                </Col>
                            </FormGroup>

                            {!!streamUuid && (
                                <div>
                                    <div className={`row brand-website-field my-1 pt-3 ${(brandDetails && !websiteConfirmation) ? 'show-confirmation' : 'mb-3'}`}>
                                        <Col sm={4}>
                                            <div className="d-flex gap-3">
                                                {
                                                    isGeneratingDetails ? (
                                                        <span className="brand-website-generating-icon">
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden>
                                                                <path d="M16 28.8691C14.6369 22.4318 9.569 17.3634 3.13184 16C9.56869 14.6366 14.6366 9.56869 16 3.13183C17.3634 9.569 22.4318 14.6369 28.8691 16C22.4315 17.3631 17.3631 22.4315 16 28.8691Z" fill="url(#paint0_linear_4717_5309)" stroke="#FFCFDE" strokeWidth="0.727273" />
                                                                <defs>
                                                                    <linearGradient id="paint0_linear_4717_5309" x1="17.3333" y1="25.3333" x2="1.77779" y2="6.22222" gradientUnits="userSpaceOnUse">
                                                                        <stop stopColor="#FFABC2" />
                                                                        <stop offset="0.461569" stopColor="#FF4360" />
                                                                        <stop offset="0.694356" stopColor="#FF267D" />
                                                                    </linearGradient>
                                                                </defs>
                                                            </svg>
                                                        </span>
                                                    ) : (
                                                        <div className="w-40px">
                                                            <WebIcon className="me-2" />
                                                        </div>
                                                    )
                                                }
                                                <div className="d-flex flex-column gap-2">
                                                    <Label className="input-label mb-0">
                                                        Website Link <span className="text-danger">*</span>
                                                    </Label>
                                                    <FieldInstruction instruction={FIELDS_INSTRUCTIONS.BRAND.WEBSITE} />
                                                </div>
                                            </div>
                                        </Col>
                                        <Col sm={8}>
                                            <GeneratingFieldWrap
                                                generating={isGeneratingDetails}
                                                isEmpty={!formFields.values.website_url?.trim()}
                                                alwaysAnimate
                                            >
                                                <Input
                                                    type="text"
                                                    name="website_url"
                                                    className="form-control"
                                                    onChange={formFields.handleChange}
                                                    onBlur={formFields.handleBlur}
                                                    value={formFields.values.website_url}
                                                    invalid={showAllFields && formFields.touched.website_url && formFields.errors.website_url ? true : false}
                                                    placeholder="Enter Website Link"
                                                    disabled={showWebsiteOnly}
                                                />
                                            </GeneratingFieldWrap>
                                            {showAllFields && formFields.touched.website_url && formFields.errors.website_url && (
                                                <FormFeedback type="invalid">
                                                    {formFields.errors.website_url}
                                                </FormFeedback>
                                            )}
                                        </Col>
                                        {isAwaitingWebsiteConfirmation && (
                                            <div className="brand-website-confirmation mt-2">
                                                <p className="brand-website-confirmation__text mb-0">
                                                    Please confirm if this is your website
                                                </p>
                                                <div className="brand-website-confirmation__actions">
                                                    <Button
                                                        type="button"
                                                        className="brand-website-confirmation__btn brand-website-confirmation__btn--yes"
                                                        onClick={handleWebsiteConfirm}
                                                    >
                                                        Yes
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        className="brand-website-confirmation__btn brand-website-confirmation__btn--no"
                                                        onClick={handleWebsiteReject}
                                                    >
                                                        No
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {showAllFields && (
                                <>
                                    <Fields
                                        formFields={formFields}
                                        onBrandLogoChange={setBrandLogo}
                                        hideName
                                        hideWebsite
                                        igProbables={igProbables}
                                        showIGProbables={websiteConfirmation === 'accepted'}
                                    />
                                </>
                            )}
                        </div>

                        {!isAwaitingWebsiteConfirmation && websiteConfirmation !== 'rejected' && (
                            <div className="brand-create-card__hint">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                                    <path d="M8 14.4395C7.31963 11.2172 4.78366 8.68073 1.56152 8C4.78335 7.31934 7.31934 4.78335 8 1.56152C8.68073 4.78366 11.2172 7.31963 14.4395 8C11.2169 8.68044 8.68044 11.2169 8 14.4395Z" fill="url(#brandCreateSparkleGradient)" stroke="#FFCFDE" strokeWidth="0.363636" />
                                    <defs>
                                        <linearGradient id="brandCreateSparkleGradient" x1="8.66667" y1="12.6667" x2="0.888891" y2="3.11111" gradientUnits="userSpaceOnUse">
                                            <stop stopColor="#FFABC2" />
                                            <stop offset="0.461569" stopColor="#FF4360" />
                                            <stop offset="0.694356" stopColor="#FF267D" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                                <span>
                                    {
                                        isGeneratingDetails
                                            ? 'Fetching brand details...'
                                            : isAwaitingWebsiteConfirmation
                                                ? 'Please confirm if the website link is correct to continue.'
                                                : isFormReady
                                                    ? <>Our AI has fetched your brand details, please review and update if needed.</>
                                                    : <>Enter your brand name and press the check or <b>Enter</b>, and our AI will pull relevant details to simplify and speed up your onboarding</>
                                    }
                                </span>
                            </div>
                        )}
                        <div className="white-blur-center bottom"></div>
                    </div>

                    <div className="d-flex justify-content-between my-4">
                        <Link to={{ pathname: '/brand' }} type="button" className="theme-btn theme-btn-lg btn-primary-outlined">
                            <i className="bx fs-4 bx-left-arrow-alt me-2"></i>Back
                        </Link>

                        {isFormReady && (
                            <button type="submit" disabled={saving} className="theme-btn theme-btn-lg btn-gradient form-continue-btn">
                                {saving ? (
                                    <Spinner size="sm" className="me-2">
                                        Loading...
                                    </Spinner>
                                ) : (
                                    <React.Fragment>
                                        Save <i className="bx fs-4 bx-right-arrow-alt"></i>
                                    </React.Fragment>
                                )}
                            </button>
                        )}
                    </div>
                </Form>
            </div>
        </div>
    );
};

export default withAccessControl(withRouter(NewBrand), PRIVILEGE.BRAND.BRAND.CREATE);
